import React, { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Download, Trash2, Cpu } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import FileTypeBadge from "./FileTypeBadge";
import IngestFileDetail from "./IngestFileDetail";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtSize = (n) => (n == null ? null : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** One version chain: (client, study, type) with its versions newest first. */
export default function IngestFileGroup({ group, expanded, onToggle, onChanged }) {
    const versions = group.versions || [];
    const current = group.current || versions[0];
    const key = `${group.company_id}::${group.identity}::${group.file_type}`;
    return (
        <div className="rounded-md border border-white/10 bg-[#111111]" data-testid={`ingest-group-${group.file_type}-${group.identity}`}>
            <button type="button" onClick={onToggle} className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-white/[0.03]" data-testid={`ingest-group-toggle-${key}`}>
                {expanded ? <ChevronDown className="w-4 h-4 text-white/40" strokeWidth={1.5} /> : <ChevronRight className="w-4 h-4 text-white/40" strokeWidth={1.5} />}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <FileTypeBadge type={group.file_type} />
                        <span className="text-sm text-white truncate" data-testid={`ingest-group-title-${key}`}>{current?.title || group.title || group.identity}</span>
                        <span className="font-mono text-[11px] text-white/40">· {group.identity}</span>
                        <span className="font-mono text-[11px] text-white/40">· {group.company_id}</span>
                    </div>
                    {current && (
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-white/50">
                            <span>current <span className="font-mono text-white/80">{current.version_label}</span></span>
                            <span>{fmtDate(current.ingested_at)}</span>
                            {current.framework_id && <span>{current.family_count} families · {current.field_count} fields</span>}
                            {current.key_topics?.length > 0 && !current.framework_id && <span className="truncate max-w-[40ch]">{current.key_topics.slice(0, 4).join(" · ")}</span>}
                            {(current.processing_warnings || []).length > 0 && (
                                <span className="text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" strokeWidth={1.75} />{current.processing_warnings.length} warning{current.processing_warnings.length === 1 ? "" : "s"}</span>
                            )}
                        </div>
                    )}
                </div>
                <span className="font-mono text-[11px] text-white/40">{versions.length} version{versions.length === 1 ? "" : "s"}</span>
            </button>
            {expanded && (
                <div className="border-t border-white/10">
                    {versions.map((v) => <VersionRow key={v.id} version={v} onChanged={onChanged} />)}
                </div>
            )}
        </div>
    );
}

function VersionRow({ version, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const toggle = async () => {
        if (detail) { setOpen(!open); return; }
        setBusy(true);
        try {
            const { data } = await api.get(`/admin/ingest-files/${version.id}`);
            setDetail(data);
            setOpen(true);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
    };

    const download = async () => {
        try {
            const res = await api.get(`/admin/ingest-files/${version.id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = version.source_filename || "file";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error("Download failed — this version has no stored bytes."); }
    };

    const remove = async () => {
        if (!window.confirm(`Delete ${version.file_type_label} ${version.version_label}?\nThis is a hard delete${version.framework_id ? " — the extracted EDC framework goes with it" : ""}.`)) return;
        try {
            await api.delete(`/admin/ingest-files/${version.id}`);
            toast.success("Deleted");
            onChanged();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    return (
        <div className="border-t border-white/5" data-testid={`ingest-version-${version.id}`}>
            <div className="flex items-center gap-3 px-5 py-2.5 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded border text-[10px] ${version.is_current_version ? "border-emerald-400/30 text-emerald-400 bg-emerald-400/10" : "border-white/15 text-white/40"}`} data-testid={`ingest-version-state-${version.id}`}>
                    {version.is_current_version ? "CURRENT" : "older"}
                </span>
                <span className="font-mono text-sm text-white/80" data-testid={`ingest-version-label-${version.id}`}>{version.version_label}</span>
                <span className="text-xs text-white/50">{fmtDate(version.ingested_at)}</span>
                <span className="text-[11px] font-mono text-white/40 truncate max-w-[32ch]" title={version.source_filename}>{version.source_filename}</span>
                {fmtSize(version.size_bytes) && <span className="text-[11px] text-white/35">{fmtSize(version.size_bytes)}</span>}
                {version.framework_id && <span className="text-[11px] text-violet-300 flex items-center gap-1"><Cpu className="w-3 h-3" strokeWidth={1.75} />{version.family_count} families · {version.field_count} fields</span>}
                {!version.stored && <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-white/35" title="Ingested before Ingest Files existed — the bytes were not kept">no stored file</span>}
                <span className="ml-auto flex items-center gap-1">
                    <button type="button" onClick={toggle} disabled={busy} data-testid={`ingest-version-toggle-${version.id}`} className="px-2 py-1 rounded text-xs text-white/70 hover:text-white hover:bg-white/5">
                        {busy ? "…" : open ? "Hide" : "Details"}
                    </button>
                    {version.stored && (
                        <button type="button" onClick={download} title="Download" data-testid={`ingest-version-download-${version.id}`} className="p-1 rounded text-white/40 hover:text-white hover:bg-white/5">
                            <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                    )}
                    <button type="button" onClick={remove} title="Delete" data-testid={`ingest-version-delete-${version.id}`} className="p-1 rounded text-white/40 hover:text-red-400 hover:bg-white/5">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                </span>
            </div>
            {(version.processing_warnings || []).length > 0 && (
                <div className="px-5 pb-2 flex items-start gap-2 text-[11px] text-amber-300" data-testid={`ingest-version-warnings-${version.id}`}>
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div>{version.processing_warnings.map((w, i) => <div key={i}>· {w}</div>)}</div>
                </div>
            )}
            {open && detail && <IngestFileDetail file={detail} />}
        </div>
    );
}
